/**
 * React hooks for NowRamp Onramp API
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  RampApi,
  type RampApiConfig,
  type OnrampSupported,
  type QuotesResponse,
  type GetQuotesParams,
  type GetSupportedParams,
  type CheckoutIntent,
  type CreateCheckoutIntentParams,
  type Transaction,
  type FlowType,
} from '@nowramp/sdk';

// =============================================================================
// Types
// =============================================================================

export interface UseRampConfigResult {
  config: OnrampSupported | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export interface UseQuotesResult {
  quotes: QuotesResponse | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export interface UseCheckoutIntentResult {
  order: CheckoutIntent | null;
  loading: boolean;
  error: Error | null;
  createOrder: (params: CreateCheckoutIntentParams) => Promise<CheckoutIntent>;
  reset: () => void;
}

export interface UseTransactionResult {
  status: Transaction | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

// =============================================================================
// Hooks
// =============================================================================

/**
 * Hook to fetch onramp supported configuration
 * Returns gateways, currencies, and payment methods
 */
export function useRampConfig(
  config: Pick<RampApiConfig, 'apiUrl' | 'projectId'>,
  orderType?: FlowType,
  country?: string
): UseRampConfigResult {
  const [data, setData] = useState<OnrampSupported | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const apiRef = useRef<RampApi | null>(null);

  // Memoize API instance
  if (!apiRef.current || apiRef.current !== config) {
    apiRef.current = new RampApi({ apiKey: '', ...config });
  }

  const fetchConfig = useCallback(async () => {
    if (!config.projectId) {
      setError(new Error('projectId is required'));
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const supportedParams: GetSupportedParams = {};
      if (orderType) supportedParams.orderType = orderType;
      if (country) supportedParams.country = country;
      const result = await apiRef.current!.getSupported(supportedParams);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch config'));
    } finally {
      setLoading(false);
    }
  }, [config.projectId, orderType, country]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  return { config: data, loading, error, refetch: fetchConfig };
}

/**
 * Hook to fetch quotes from all gateways
 * Auto-refreshes when params change
 */
export function useQuotes(
  config: Pick<RampApiConfig, 'apiUrl' | 'projectId'>,
  params: GetQuotesParams | null
): UseQuotesResult {
  const [data, setData] = useState<QuotesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const apiRef = useRef<RampApi | null>(null);
  const configKeyRef = useRef('');

  // Recreate API instance when config changes
  const configKey = `${config.apiUrl}|${config.projectId}`;
  if (!apiRef.current || configKeyRef.current !== configKey) {
    apiRef.current = new RampApi({ apiKey: '', ...config });
    configKeyRef.current = configKey;
  }

  const fetchIdRef = useRef(0);

  useEffect(() => {
    // Bump fetch ID on every param change to invalidate in-flight requests
    const id = ++fetchIdRef.current;

    if (!params || !config.projectId) {
      setData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    apiRef.current!.getQuotes(params).then(
      (result) => {
        if (fetchIdRef.current === id) {
          setData(result);
          setLoading(false);
        }
      },
      (err) => {
        if (fetchIdRef.current === id) {
          setError(err instanceof Error ? err : new Error('Failed to fetch quotes'));
          setData(null);
          setLoading(false);
        }
      }
    );
  }, [config.projectId, params?.fiatCurrency, params?.fiatAmount, params?.cryptoAmount, params?.cryptoCurrency, params?.network, params?.paymentMethodId, params?.country, params?.orderType]);

  const refetch = useCallback(async () => {
    if (!params || !config.projectId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await apiRef.current!.getQuotes(params);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch quotes'));
    } finally {
      setLoading(false);
    }
  }, [config.projectId, params]);

  return { quotes: data, loading, error, refetch };
}

/**
 * Hook to create a checkout intent
 */
export function useCheckoutIntent(
  config: Pick<RampApiConfig, 'apiUrl' | 'projectId'>
): UseCheckoutIntentResult {
  const [order, setOrder] = useState<CheckoutIntent | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const apiRef = useRef<RampApi | null>(null);
  const configKeyRef = useRef('');

  // Recreate API instance when config changes
  const configKey = `${config.apiUrl}|${config.projectId}`;
  if (!apiRef.current || configKeyRef.current !== configKey) {
    apiRef.current = new RampApi({ apiKey: '', ...config });
    configKeyRef.current = configKey;
  }

  const createOrder = useCallback(async (params: CreateCheckoutIntentParams): Promise<CheckoutIntent> => {
    setLoading(true);
    setError(null);

    try {
      const result = await apiRef.current!.createCheckoutIntent(params);
      setOrder(result);
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to create checkout intent');
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setOrder(null);
    setError(null);
  }, []);

  return { order, loading, error, createOrder, reset };
}

/**
 * Hook to poll transaction status
 */
export function useTransaction(
  config: Pick<RampApiConfig, 'apiUrl'>,
  transactionId: string | null,
  options?: { pollInterval?: number; initialDelay?: number }
): UseTransactionResult {
  const [status, setStatus] = useState<Transaction | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const apiRef = useRef<RampApi | null>(null);

  if (!apiRef.current) {
    apiRef.current = new RampApi({ apiKey: '', projectId: '', ...config });
  }

  const fetchStatus = useCallback(async () => {
    if (!transactionId) return;

    setLoading(true);
    setError(null);

    try {
      const result = await apiRef.current!.getTransaction(transactionId);
      setStatus(result);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch status'));
    } finally {
      setLoading(false);
    }
  }, [transactionId]);

  // Initial fetch (with optional delay to let checkout iframe load first)
  useEffect(() => {
    if (!transactionId) return;

    const delay = options?.initialDelay ?? 0;
    if (delay > 0) {
      const timer = setTimeout(fetchStatus, delay);
      return () => clearTimeout(timer);
    }

    fetchStatus();
  }, [fetchStatus, transactionId, options?.initialDelay]);

  // Polling — only start after the initial (possibly delayed) fetch has
  // returned a status.  Without the `!status` guard the setInterval would
  // bypass initialDelay entirely, firing its first callback at pollInterval
  // (e.g. 5 s) even though we asked for a 15 s delay.
  useEffect(() => {
    if (!transactionId || !options?.pollInterval) return;

    // Wait for the initial fetch to populate status before polling
    if (!status) return;

    // Stop polling if order is in terminal state
    const terminalStatuses = ['completed', 'failed', 'cancelled', 'expired', 'refunded'];
    if (terminalStatuses.includes(status.status)) {
      return;
    }

    const interval = setInterval(fetchStatus, options.pollInterval);
    return () => clearInterval(interval);
  }, [transactionId, options?.pollInterval, status?.status, fetchStatus]);

  return { status, loading, error, refetch: fetchStatus };
}
